import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

interface QueryExportDialogProps {
    open: boolean;
    defaultEmail?: string;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onConfirm: (params: { notify: string[]; canal: string }) => void;
}

const CHANNEL_OPTIONS = [
    { value: '', label: 'Tous les canaux' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'courrier', label: 'Courrier' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (value: string) => (
    value
        .split(/[\n,;]+/)
        .map(email => email.trim())
        .filter(Boolean)
);

function QueryExportDialog({
    open,
    defaultEmail = '',
    loading = false,
    error,
    onClose,
    onConfirm,
}: QueryExportDialogProps) {
    const [emailText, setEmailText] = useState(defaultEmail);
    const [canal, setCanal] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setEmailText(defaultEmail);
        setCanal('');
        setValidationError(null);
    }, [open, defaultEmail]);

    const emails = useMemo(() => parseEmails(emailText), [emailText]);

    const handleConfirm = () => {
        if (emails.length === 0) {
            setValidationError('Veuillez renseigner au moins un email de destination.');
            return;
        }

        const invalidEmail = emails.find(email => !EMAIL_PATTERN.test(email));
        if (invalidEmail) {
            setValidationError(`Email invalide : ${invalidEmail}`);
            return;
        }

        setValidationError(null);
        onConfirm({ notify: emails, canal });
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Recevoir ma sélection</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info">
                        L'export sera envoyé par email une fois le traitement terminé.
                    </Alert>

                    {(error || validationError) && (
                        <Alert severity="error">
                            {validationError || error}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Champs obligatoires
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Civilité / Nom / Prénom / Numéro de carte de fidélité
                        </Typography>
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Canal</InputLabel>
                        <Select
                            value={canal}
                            label="Canal"
                            onChange={(event) => setCanal(event.target.value)}
                            disabled={loading}
                        >
                            {CHANNEL_OPTIONS.map(option => (
                                <MenuItem key={option.value || 'all'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Email de destination"
                        value={emailText}
                        onChange={(event) => setEmailText(event.target.value)}
                        disabled={loading}
                        fullWidth
                        multiline
                        minRows={2}
                        helperText="Séparez plusieurs emails par une virgule, un point-virgule ou une nouvelle ligne."
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Retour
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {loading ? 'Demande en cours...' : 'Valider'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QueryExportDialog;
